// Generated by TaroBase SDK Generator: 2026-07-24T09:46:28.101Z
// Collection: agentCreations/$agentId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Credit-DEDUCTION gate for creating an agent. isPassthrough onchain record: executes the credit debit atomically onchain and stays queryable offchain. One agentCreations doc (id == the agent id) authorizes exactly one agents doc create for non-admins. Admins (ADMIN_CREATOR_ADDRESS / ADMIN_ADDRESS / PROJECT_VAULT_ADDRESS) short-circuit with zero cost and are never blocked. Cost = AGENT_BASE_COST + AGENT_PER_CAPABILITY_COST * capabilityCount. A missing userCredits record is treated as 50.
 */

export interface AgentCreationsRequest {
  creator: AddressType;
  capabilityCount: number | TimeOperation | IncrementOperation | TokenAmount;
  cost: number | TimeOperation | IncrementOperation | TokenAmount;
}

export interface AgentCreationsResponse {
  creator: string;
  capabilityCount: number;
  cost: number;
  id: string;
  tarobase_created_at: number;
  tarobase_transaction_hash?: string | undefined; // Represents the hash of the transaction that created this item. Can be used to create Solscan links (combine with the current network in the Tarobase app)
}

/**
 * Build a AgentCreations operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildAgentCreations(agentId: string, data: AgentCreationsRequest): DocumentOperation {
  const doc = transformValues(data);
  return { path: `agentCreations/${agentId}`, document: doc };
}

/**
 * Admins (ADMIN_CREATOR_ADDRESS, ADMIN_ADDRESS, or PROJECT_VAULT_ADDRESS) may create at ZERO cost and are NEVER blocked. Non-admins: @newData.creator MUST equal @user.address, and the creator's userCredits balance (a MISSING record is treated as NEW_WALLET_CREDITS=50) MUST be >= AGENT_BASE_COST + AGENT_PER_CAPABILITY_COST * capabilityCount. The onchain create hook then, for non-admins ONLY, atomically debits that exact computed cost from the creator's userCredits.balance via @DocumentPlugin.updateField — initializing a missing record to 50 first (so a fresh wallet's balance becomes 50 - cost). Admins short-circuit the hook with no deduction. isPassthrough: runs onchain, record stays queryable offchain. Frontend must pass the SAME doc id as the agent it authorizes. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setAgentCreations(agentId: string, data: AgentCreationsRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`agentCreations/${agentId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting AgentCreations: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Public. Anyone can read agent-creation records (creator, capabilityCount, cost). Required for onchain collections; agents create rule reads this via get() to authorize the paired agent doc.
   (Get Single Item)
 */
export async function getAgentCreations(agentId: string): Promise<AgentCreationsResponse | null> {
  try {
    const result = await get(`agentCreations/${agentId}`);
    return result as AgentCreationsResponse | null;
  } catch (error) {
    console.error(`Error getting AgentCreations: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single AgentCreations document. (
  Read Operation Details: Public. Anyone can read agent-creation records (creator, capabilityCount, cost). Required for onchain collections; agents create rule reads this via get() to authorize the paired agent doc.
  )
 */
export function subscribeAgentCreations(callback: (data: AgentCreationsResponse | null) => void, agentId: string): Promise<() => Promise<void>> {
  return subscribe(`agentCreations/${agentId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as AgentCreationsResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to AgentCreations at path '${`agentCreations/${agentId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many AgentCreations items from collection agentCreations
 
  Read Operation Details: Public. Anyone can read agent-creation records (creator, capabilityCount, cost). Required for onchain collections; agents create rule reads this via get() to authorize the paired agent doc.
  
 */
export async function getManyAgentCreations(filter: string = ""): Promise<AgentCreationsResponse[]> {
  try {
    const results = await get("agentCreations", { prompt: filter });
    return Array.isArray(results) ? results as AgentCreationsResponse[] : [];
  } catch (error) {
    console.error(`Error getting AgentCreations collection at path 'agentCreations': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in AgentCreations collection at agentCreations
 
  Read Operation Details: Public. Anyone can read agent-creation records (creator, capabilityCount, cost). Required for onchain collections; agents create rule reads this via get() to authorize the paired agent doc.
  
 */
export function subscribeManyAgentCreations(callback: (data: AgentCreationsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("agentCreations", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as AgentCreationsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to AgentCreations collection at path '${"agentCreations"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all AgentCreations items from collection agentCreations
 
  Read Operation Details: Public. Anyone can read agent-creation records (creator, capabilityCount, cost). Required for onchain collections; agents create rule reads this via get() to authorize the paired agent doc.
  
 */
export async function getAllAgentCreations(filter: string = ""): Promise<AgentCreationsResponse[]> {
  try {
    const results = await get("agentCreations", { prompt: filter });
    return Array.isArray(results) ? results as AgentCreationsResponse[] : [];
  } catch (error) {
    console.error(`Error getting AgentCreations collection at path 'agentCreations': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in AgentCreations collection at agentCreations
 
  Read Operation Details: Public. Anyone can read agent-creation records (creator, capabilityCount, cost). Required for onchain collections; agents create rule reads this via get() to authorize the paired agent doc.
  
 */
export function subscribeAllAgentCreations(callback: (data: AgentCreationsResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("agentCreations", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as AgentCreationsResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to AgentCreations collection at path '${"agentCreations"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count AgentCreations items in collection agentCreations.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countAgentCreations(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("agentCreations", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting AgentCreations at path 'agentCreations': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on AgentCreations items in collection agentCreations.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateAgentCreations(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("agentCreations", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating AgentCreations at path 'agentCreations': ${formatError(error)}`);
    return { value: 0 };
  }
}

